import { useEffect, useMemo, useState } from "react";
import { Search, Filter, Lock, BookOpen, Calendar, User, X, Send } from "lucide-react";
import api, { fileUrl, formatApiError } from "@/lib/api";
import PdfViewer from "@/components/PdfViewer";

const DEFAULTS = {
  eyebrow: "Bibliothèque numérique",
  title: "Bibliothèques numériques",
  banner_image: "/media/digital-library.jpg",
  intro:
    "Les Bibliothèques numériques de l'UNISIC proposent aux étudiants, enseignants et chercheurs un accès organisé aux ressources documentaires numérisées.",
};

export default function BibliothequesNumeriques() {
  const [content, setContent] = useState(DEFAULTS);
  const [q, setQ] = useState("");
  const [year, setYear] = useState("");
  const [author, setAuthor] = useState("");
  const [docType, setDocType] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewer, setViewer] = useState(null); // {title, url}
  const [subFor, setSubFor] = useState(null); // publication for subscription modal

  useEffect(() => {
    api.get("/content/service.bibliotheques_numeriques").then((r) => setContent({ ...DEFAULTS, ...(r.data || {}) })).catch(() => {});
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (year) params.year = year;
      if (author) params.author = author;
      if (docType) params.doc_type = docType;
      const { data } = await api.get("/publications", { params });
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  const submit = (e) => { e.preventDefault(); load(); };
  const reset = () => { setQ(""); setYear(""); setAuthor(""); setDocType(""); setTimeout(load, 0); };

  const openPub = (p) => {
    if (p.requires_subscription) {
      setSubFor(p);
      return;
    }
    setViewer({ title: p.title, url: fileUrl(p.file_path), filename: `${p.title}.pdf` });
  };

  return (
    <div data-testid="biblio-num-page">
      <section className="relative h-[42vh] min-h-[300px] overflow-hidden">
        <img src={content.banner_image} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-veil" />
        <div className="relative h-full mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-12">
          <div className="eyebrow" style={{ color: "#E5C158" }}>{content.eyebrow}</div>
          <h1 className="mt-3 text-white text-4xl sm:text-5xl font-bold tracking-tight">{content.title}</h1>
          <p className="mt-3 text-white/85 max-w-3xl">{content.intro}</p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-unisic-gold" />
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
        <form onSubmit={submit} className="paper-card p-6" data-testid="biblio-num-search">
          <div className="grid gap-4 md:grid-cols-5">
            <div className="md:col-span-2">
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">Rechercher</label>
              <div className="mt-1 relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Titre, mots-clés..." className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="input-q" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">Auteur</label>
              <input value={author} onChange={(e) => setAuthor(e.target.value)} className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm" data-testid="filter-author" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">Année</label>
              <input value={year} onChange={(e) => setYear(e.target.value)} placeholder="ex: 2024" className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm" data-testid="filter-year" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">Type</label>
              <select value={docType} onChange={(e) => setDocType(e.target.value)} className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm bg-white" data-testid="filter-type">
                <option value="">Tous</option>
                <option value="ouvrage">Ouvrage</option>
                <option value="article">Article</option>
                <option value="thèse">Thèse</option>
                <option value="revue">Revue</option>
                <option value="autre">Autre</option>
              </select>
            </div>
          </div>
          <div className="mt-5 flex justify-between items-center">
            <div className="text-xs text-slate-500 flex items-center gap-2">
              <Filter className="w-3 h-3" /> {items.length} publication(s)
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={reset} className="text-sm text-slate-600 hover:text-unisic-blue">Réinitialiser</button>
              <button type="submit" className="btn-primary text-sm" data-testid="btn-search"><Search className="w-4 h-4" /> Rechercher</button>
            </div>
          </div>
        </form>

        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {loading && <div className="col-span-full text-center py-12 text-slate-500 text-sm">Chargement...</div>}
          {!loading && items.length === 0 && (
            <div className="col-span-full text-center py-16 paper-card p-10">
              <BookOpen className="w-8 h-8 text-slate-300 mx-auto" />
              <p className="mt-4 text-slate-500">Aucune publication numérique disponible pour l'instant.</p>
            </div>
          )}
          {items.map((p) => (
            <div key={p.id} className="paper-card card-hover p-6 flex flex-col" data-testid={`pub-item-${p.id}`}>
              {p.cover_image ? (
                <img src={p.cover_image} alt="" className="w-full h-40 object-cover rounded-sm mb-4" />
              ) : (
                <div className="w-full h-40 bg-gradient-to-br from-unisic-blue/90 to-unisic-blue rounded-sm mb-4 flex items-center justify-center">
                  <BookOpen className="w-10 h-10 text-white/70" />
                </div>
              )}
              <div className="text-[11px] uppercase tracking-widest text-unisic-gold font-semibold flex items-center gap-2">
                {p.doc_type || "Ouvrage"}
                {p.requires_subscription && <span className="inline-flex items-center gap-1 bg-unisic-gold/15 text-unisic-blue px-2 py-0.5 rounded"><Lock className="w-3 h-3" /> Abonnement</span>}
              </div>
              <h3 className="mt-2 font-display font-semibold text-unisic-blue leading-snug">{p.title}</h3>
              {p.abstract && <p className="mt-3 text-sm text-slate-600 line-clamp-3 flex-1">{p.abstract}</p>}
              <div className="mt-4 pt-4 border-t border-slate-100 space-y-1.5 text-xs text-slate-600">
                <div className="flex items-center gap-2"><User className="w-3.5 h-3.5 text-slate-400" /> {p.authors || "—"}</div>
                <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-slate-400" /> {p.year || "—"}</div>
              </div>
              <button onClick={() => openPub(p)} className="mt-5 btn-secondary text-sm justify-center" data-testid={`btn-open-pub-${p.id}`}>
                {p.requires_subscription ? (<><Lock className="w-4 h-4" /> Demander l'accès</>) : (<><BookOpen className="w-4 h-4" /> Lire en ligne</>)}
              </button>
            </div>
          ))}
        </div>
      </section>

      {viewer && (
        <PdfModal title={viewer.title} url={viewer.url} filename={viewer.filename} onClose={() => setViewer(null)} />
      )}
      {subFor && (
        <SubscriptionModal publication={subFor} onClose={() => setSubFor(null)} />
      )}
    </div>
  );
}

function PdfModal({ title, url, filename, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center px-2 sm:px-6 py-6 overflow-auto" onClick={onClose} data-testid="pdf-modal">
      <div className="bg-white max-w-4xl w-full rounded-sm" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h3 className="font-display font-semibold text-unisic-blue truncate">{title}</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded" data-testid="close-pdf-modal"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4">
          <PdfViewer url={url} filename={filename} />
        </div>
      </div>
    </div>
  );
}

function SubscriptionModal({ publication, onClose }) {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", affiliation: "", reason: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const update = (k, v) => setForm({ ...form, [k]: v });
  const submit = async (e) => {
    e.preventDefault();
    setSending(true); setError("");
    try {
      await api.post("/subscription-requests", { ...form, publication_id: publication.id });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-4" onClick={onClose} data-testid="subscription-modal">
      <div className="bg-white max-w-lg w-full rounded-sm" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div>
            <div className="eyebrow eyebrow-dark">Contenu réservé</div>
            <h3 className="mt-1 font-display font-semibold text-unisic-blue">Demande d'abonnement</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
        </div>
        {done ? (
          <div className="p-8 text-center">
            <div className="w-12 h-12 rounded-sm bg-green-100 text-green-700 mx-auto mb-4 flex items-center justify-center">
              <Send className="w-5 h-5" />
            </div>
            <p className="text-sm text-slate-700">Votre demande a bien été transmise. Le Service des Bibliothèques vous contactera après examen.</p>
            <button onClick={onClose} className="mt-6 btn-secondary text-sm">Fermer</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4" data-testid="subscription-form">
            <div className="rounded-sm bg-slate-50 border border-slate-200 p-3 text-sm">
              <div className="text-[11px] uppercase tracking-widest text-slate-500">Ouvrage demandé</div>
              <div className="mt-1 text-unisic-blue font-semibold">{publication.title}</div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">Nom complet</label>
              <input required value={form.full_name} onChange={(e) => update("full_name", e.target.value)} className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm" data-testid="sub-name" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">E-mail</label>
              <input required type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm" data-testid="sub-email" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">Téléphone</label>
                <input value={form.phone} onChange={(e) => update("phone", e.target.value)} className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">Affiliation</label>
                <input value={form.affiliation} onChange={(e) => update("affiliation", e.target.value)} className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm" placeholder="Institution, faculté..." />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">Motif</label>
              <textarea rows={3} value={form.reason} onChange={(e) => update("reason", e.target.value)} className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm" placeholder="Expliquez brièvement l'usage prévu..." />
            </div>
            {error && <div className="text-xs text-unisic-red" data-testid="sub-error">{error}</div>}
            <button type="submit" disabled={sending} className="btn-primary text-sm w-full justify-center" data-testid="btn-submit-sub">
              <Send className="w-4 h-4" /> {sending ? "Envoi..." : "Envoyer la demande"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
